'use client';

import { useEffect } from 'react';

const SCRIPT_ATTR = 'data-site-json-ld';

export interface SiteJsonLdInput {
  /** Skip injection in the builder preview and on the platform host. */
  enabled: boolean;
  name: string;
  url: string;
  telephone?: string;
  email?: string;
  address?: string;
  city?: string;
  logoUrl?: string;
  description?: string;
}

/**
 * Publishes an `AutoRental` organization record for search engines. The script
 * is tagged so navigating between storefront pages replaces it instead of
 * stacking duplicate structured-data blocks.
 */
export function useSiteJsonLd(input: SiteJsonLdInput): void {
  const { enabled, name, url, telephone, email, address, city, logoUrl, description } = input;

  useEffect(() => {
    const previous = document.head.querySelector<HTMLScriptElement>(`script[${SCRIPT_ATTR}]`);
    previous?.remove();

    if (!enabled || !name.trim() || !url.trim()) return;

    const organization: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'AutoRental',
      '@id': `${url.replace(/\/$/, '')}/#organization`,
      name: name.trim(),
      url,
    };

    if (telephone?.trim()) organization.telephone = telephone.trim();
    if (email?.trim()) organization.email = email.trim();
    if (logoUrl?.trim()) {
      try {
        organization.image = new URL(logoUrl.trim(), url).toString();
      } catch {
        organization.image = logoUrl.trim();
      }
    }
    if (description?.trim()) organization.description = description.trim();
    if (address?.trim() || city?.trim()) {
      organization.address = {
        '@type': 'PostalAddress',
        ...(address?.trim() ? { streetAddress: address.trim() } : {}),
        ...(city?.trim() ? { addressLocality: city.trim() } : {}),
      };
    }
    if (city?.trim()) organization.areaServed = city.trim();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(SCRIPT_ATTR, '1');
    script.textContent = JSON.stringify(organization);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [enabled, name, url, telephone, email, address, city, logoUrl, description]);
}
